import React from "react";
import FilterBar from "@/components/common/FilterBar";
import StickyFilterBar from "@/components/common/StickyFilterBar";
import { calculateDateRange, DATE_RANGE_KEYS, getRecalculatedDatesIfNeeded } from "@/utils/dateRangeUtils";
import { DateRangeChange, FilterChangeEvent } from "@/types";
import { useDashboardData } from "@/contexts/DashboardContext";
import { useFilterCache } from "@/hooks/useFilterCache";
import UserTotalWidget from "@/components/widgets/users/UserTotalWidget";
import UserAvgSlaWidget from "@/components/widgets/users/UserAvgSlaWidget";
import UserCallsHandledWidget from "@/components/widgets/users/UserCallsHandledWidget";
import UserAvgHandleTimeWidget from "@/components/widgets/users/UserAvgHandleTimeWidget";
import UserPerformanceTableWidget from "@/components/widgets/users/UserPerformanceTableWidget";
import UserSlaRankingWidget from "@/components/widgets/users/UserSlaRankingWidget";
import UserActivitiesChartWidget from "@/components/widgets/users/UserActivitiesChartWidget";
import { fetchUserWidgetsBatch, UserWidgetsBatchResult } from "@/lib/axios/api/widgets.api";
import { useAppData, UserRole } from "@/contexts/AppDataContext";
import { EMPTY_QUEUE_ID, EMPTY_AGENT_ID } from "@/constants/queue";
import { getAgentIds } from "@/utils/agentUtils";

const UsersTab: React.FC<{ isGeneralMode?: boolean; filterClass?: string }> = ({ isGeneralMode, filterClass }) => {
  const defaultDateRangeKey = DATE_RANGE_KEYS.LAST_7_DAYS;
  const { current } = useDashboardData();
  const { filters, saveFilters } = useFilterCache("user-performance-dashboard", current?.id);
  const { queuesDataVersion, userRole, queueMicrosoftIds, uniqueAgents } = useAppData();
  const isManager = userRole === UserRole.MANAGER;

  const agentIds = React.useMemo(() => getAgentIds(uniqueAgents), [uniqueAgents]);

  // TCX-6500-style scoping (mirrors AgentsTab): for a Manager, an unfiltered ("All Agents")
  // request must still resolve to their full in-scope agent list (never []), using
  // EMPTY_AGENT_ID when that scope is empty; Admin/Partner keeps today's fallback.
  //
  // TCX-fix-role: for a Manager, a manual pick is also clamped to their *current* authorized
  // scope (mirrors Queue tab's resolveScopedIds, TCX-6502) — a cached agent id from before a
  // role/queue change that removed them from the manager's scope must fall back to "All Agents"
  // for that scope instead of being sent verbatim.
  const getAgentMicrosoftIds = React.useCallback((agents: string[]): string[] | null => {
    if (isManager) {
      const authorized = new Set(agentIds);
      const clamped = agents.filter(id => authorized.has(id));
      if (clamped.length > 0) return clamped;
      return agentIds.length > 0 ? agentIds : [EMPTY_AGENT_ID];
    }
    if (agents.length > 0) return agents;
    return agentIds;
  }, [agentIds, isManager]);

  // TCX-6500-style scoping (mirrors AgentsTab): for a Manager, an unfiltered request must send
  // their full assigned-queue scope instead of `null` ("no restriction"), using EMPTY_QUEUE_ID
  // when that scope is empty so it isn't mistaken for "no filter"; Admin/Partner keeps sending
  // `null`, unchanged.
  //
  // TCX-fix-role: same clamp as getAgentMicrosoftIds above, for the queue selection.
  const resolveQueueMicrosoftIds = React.useCallback((selectedQueues: string[]): string[] | null => {
    if (!isManager) return null;
    const authorized = new Set(queueMicrosoftIds);
    const clamped = selectedQueues.filter(id => authorized.has(id));
    if (clamped.length > 0) return clamped;
    return queueMicrosoftIds.length > 0 ? queueMicrosoftIds : [EMPTY_QUEUE_ID];
  }, [isManager, queueMicrosoftIds]);
  const [dateRangeKey, setDateRangeKey] = React.useState<string>(filters?.dateRangeKey ?? defaultDateRangeKey);
  const [dateRange, setDateRange] = React.useState<DateRangeChange>(() => {
    if (filters) {
      const { startDate, endDate, previousStartDate, previousEndDate } = getRecalculatedDatesIfNeeded(
        filters.dateRangeKey,
        filters
      );
      return { key: filters.dateRangeKey, startDate, endDate, previousStartDate, previousEndDate };
    }
    return {
      key: defaultDateRangeKey,
      startDate: null,
      endDate: null,
      previousStartDate: null,
      previousEndDate: null,
    };
  });

  const [kpiData, setKpiData] = React.useState<UserWidgetsBatchResult | null>(null);
  const [kpiLoading, setKpiLoading] = React.useState(false);
  const [kpiError, setKpiError] = React.useState(false);

  const resolvedQueueMicrosoftIds = React.useMemo(
    () => resolveQueueMicrosoftIds(filters?.queues ?? []),
    [resolveQueueMicrosoftIds, filters?.queues]
  );
  const resolvedAgentMicrosoftIds = React.useMemo(
    () => getAgentMicrosoftIds(filters?.agents ?? []),
    [getAgentMicrosoftIds, filters?.agents]
  );
  // SLA Ranking's/Average SLA's `resolvedAgentIds` (TCX-6500 FR-027): the caller's full
  // role-resolved agent scope, unintersected by the manual agent filter — keeps both widgets'
  // scope pinned to team size even when the manager narrows `agentIds` via the dropdown.
  const slaResolvedAgentMicrosoftIds = React.useMemo(
    () => getAgentMicrosoftIds([]),
    [getAgentMicrosoftIds]
  );
  // SLA Ranking's/Average SLA's `allAgentsSelected` (TCX-6503 pattern, mirrors Direct Call):
  // states outright whether the manual agent filter is empty, rather than letting the backend
  // infer it from `agentMicrosoftIds` — a Manager's resolved value is always populated (their
  // full team) whether or not they've filtered, so that alone can't tell the two states apart.
  const allAgentsSelected = !(filters?.agents?.length);

  React.useEffect(() => {
    if (!filters) {
      const dr = calculateDateRange(defaultDateRangeKey);
      setDateRange({
        key: defaultDateRangeKey,
        startDate: dr.from.toISOString(),
        endDate: dr.to.toISOString(),
        previousStartDate: dr.previousFrom.toISOString(),
        previousEndDate: dr.previousTo.toISOString(),
      });
      setDateRangeKey(defaultDateRangeKey);
    }
  }, [filters]);

  // Refresh data when tab becomes active — recalc dynamic ranges (e.g. "Today so far") to current time
  // Skip initial mount: dateRange is already fresh right after mount.
  const isFirstActiveRef = React.useRef(true);
  React.useEffect(() => {
    if (isFirstActiveRef.current) {
      isFirstActiveRef.current = false;
      return;
    }
    if (isGeneralMode) {
      setDateRange(prev => {
        if (!prev.startDate || !prev.endDate || !prev.previousStartDate || !prev.previousEndDate) return prev;
        const { startDate, endDate, previousStartDate, previousEndDate } = getRecalculatedDatesIfNeeded(prev.key, {
          startDate: prev.startDate,
          endDate: prev.endDate,
          previousStartDate: prev.previousStartDate,
          previousEndDate: prev.previousEndDate,
        });
        return { ...prev, startDate, endDate, previousStartDate, previousEndDate };
      });
    }
  }, [isGeneralMode]);

  React.useEffect(() => {
    const { startDate, endDate } = dateRange;
    if (!startDate || !endDate) return;
    const controller = new AbortController();
    setKpiLoading(true);
    setKpiError(false);
    fetchUserWidgetsBatch(
      {
        startDate,
        endDate,
        agentMicrosoftIds: resolvedAgentMicrosoftIds,
        queueMicrosoftIds: resolvedQueueMicrosoftIds,
        resolvedAgentMicrosoftIds: slaResolvedAgentMicrosoftIds,
        allAgentsSelected,
      },
      { signal: controller.signal }
    )
      .then(result => setKpiData(result))
      .catch(err => {
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;
        setKpiError(true);
      })
      .finally(() => setKpiLoading(false));
    return () => controller.abort();
  }, [dateRange.startDate, dateRange.endDate, resolvedAgentMicrosoftIds, resolvedQueueMicrosoftIds, slaResolvedAgentMicrosoftIds, allAgentsSelected, queuesDataVersion]);

  const handleFilterChange = (event: FilterChangeEvent) => {
    setDateRangeKey(event.dateRange.key);

    const { startDate, endDate, previousStartDate, previousEndDate } = getRecalculatedDatesIfNeeded(
      event.dateRange.key,
      {
        dateRangeKey: event.dateRange.key,
        startDate: event.dateRange.startDate.toISOString(),
        endDate: event.dateRange.endDate.toISOString(),
        previousStartDate: event.dateRange.previousStartDate.toISOString(),
        previousEndDate: event.dateRange.previousEndDate.toISOString(),
      }
    );

    setDateRange({
      key: event.dateRange.key,
      startDate,
      endDate,
      previousStartDate,
      previousEndDate,
    });

    saveFilters({
      dateRangeKey: event.dateRange.key,
      startDate: event.dateRange.startDate.toISOString(),
      endDate: event.dateRange.endDate.toISOString(),
      previousStartDate: event.dateRange.previousStartDate.toISOString(),
      previousEndDate: event.dateRange.previousEndDate.toISOString(),
      queues: event.queues ?? [],
      agents: event.agents ?? [],
      selectedQueueType: event.selectedQueueType,
      searchTerm: "",
    });
  };

  return (
    <div className="w-full">
      <div className="border text-primary bg-[#FCFCFD] rounded-lg dark:border-[#3C5C73] dark:bg-[#3C5C73] dark:text-white px-[8px] pb-[8px] pt-1">
        <StickyFilterBar className={filterClass}>
          <FilterBar
            onFilterChange={handleFilterChange}
            initialDateRangeKey={dateRangeKey}
            initialSelectedQueues={filters?.queues}
            initialSelectedAgents={filters?.agents}
            initialSelectedQueueType={filters?.selectedQueueType}
            initialDateRange={dateRange}
          />
        </StickyFilterBar>

        <div className="flex flex-col gap-4">
          {/* Row 1: KPI cards left + Performance table right */}
          <div className="flex gap-4">
            {/* KPI column */}
            <div className="flex flex-col gap-3 w-[280px] flex-shrink-0">
              <UserTotalWidget
                loading={kpiLoading}
                value={kpiData?.totalUsers}
              />
              <UserAvgSlaWidget
                loading={kpiLoading}
                value={kpiData?.avgSla}
              />
              <UserCallsHandledWidget
                loading={kpiLoading}
                data={kpiData?.callsHandled}
              />
              <UserAvgHandleTimeWidget loading={kpiLoading} value={kpiData?.avgHandleTime} />
            </div>

            {/* Performance table — relative+absolute keeps table out of flex height calc */}
            <div className="flex-1 min-w-0 relative">
              <div className="absolute inset-0 overflow-hidden">
                <UserPerformanceTableWidget
                  startDate={dateRange.startDate ?? undefined}
                  endDate={dateRange.endDate ?? undefined}
                  agentMicrosoftIds={resolvedAgentMicrosoftIds}
                  queueMicrosoftIds={resolvedQueueMicrosoftIds}
                  queuesDataVersion={queuesDataVersion}
                />
              </div>
            </div>
          </div>

          {/* Row 2: SLA Ranking + Activities chart */}
          <div className="grid grid-cols-1 lg:grid-cols-10 gap-4">
            <div className="h-[380px] lg:col-span-4">
              <UserSlaRankingWidget
                data={kpiData?.slaRanking}
                isLoading={kpiLoading}
                isError={kpiError}
              />
            </div>
            <div className="h-[380px] lg:col-span-6">
              <UserActivitiesChartWidget
                startDate={dateRange.startDate ?? undefined}
                endDate={dateRange.endDate ?? undefined}
                agentMicrosoftIds={resolvedAgentMicrosoftIds}
                queueMicrosoftIds={resolvedQueueMicrosoftIds}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsersTab;
